import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import axios from "../../api/axiosInstance";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

const CallbackRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchRequests = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get("/callback/all");
            setRequests(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to fetch callback requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleStatusChange = async (id, newStatus) => {
        try {
            const response = await axios.put(`/callback/${id}/status?status=${newStatus}`);
            setRequests(requests.map(req => req.id === id ? response.data : req));
        } catch (err) {
            alert("Failed to update status");
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case "pending":
                return <Chip label="Pending" size="small" color="warning" variant="outlined" />;
            case "called":
                return <Chip label="Called" size="small" color="info" variant="outlined" />;
            case "resolved":
                return <Chip label="Resolved" size="small" color="success" variant="outlined" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    if (loading && requests.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>
                    Callback Requests
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchRequests}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.1)' }}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                        <TableRow>
                            <TableCell fontWeight={700}>Date</TableCell>
                            <TableCell fontWeight={700}>Name</TableCell>
                            <TableCell fontWeight={700}>Contact</TableCell>
                            <TableCell fontWeight={700}>Status</TableCell>
                            <TableCell fontWeight={700}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requests.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    No callback requests found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            requests.map((req) => (
                                <TableRow key={req.id} hover>
                                    <TableCell>
                                        {new Date(req.created_at).toLocaleDateString()}
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            {new Date(req.created_at).toLocaleTimeString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell fontWeight={600}>{req.full_name}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <EmailIcon fontSize="small" color="disabled" />
                                                <Typography variant="body2">{req.email}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PhoneIcon fontSize="small" color="disabled" />
                                                <Typography variant="body2">{req.contact_number}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{getStatusChip(req.status)}</TableCell>
                                    <TableCell>
                                        <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
                                            <Select
                                                value={req.status}
                                                onChange={(e) => handleStatusChange(req.id, e.target.value)}
                                                sx={{ borderRadius: '8px' }}
                                            >
                                                <MenuItem value="pending">Pending</MenuItem>
                                                <MenuItem value="called">Called</MenuItem>
                                                <MenuItem value="resolved">Resolved</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default CallbackRequests;
